import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import AllQuestions from '../AllData/AllQuestions';
import "./Style/QuestionImageLoad.css";
import TopNavbar from "../home/TopNavbar";

const IMAGE_CACHE = "question-images-v1";

const QuestionImageLoad = () => {
    const { subject, topic, type } = useParams();
    const questions = AllQuestions;
    const [questionList, setQuestionList] = useState([]);

    const [downloaded, setDownloaded] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [complete, setComplete] = useState(false);
    const [error, setError] = useState("");

    // 1. Filter questions based on URL params
    useEffect(() => {
        const filteredQuestions = questions.filter(question =>
            question.subject === subject &&
            question.type === type &&
            (question.topic === topic || question.year === topic)
        );
        setQuestionList(filteredQuestions);
    }, [questions, subject, topic, type]);

    // 2. Extract dynamic image list and map them to the "questionsPhoto" folder
    const dynamicImageList = useMemo(() => {
        return questionList
            .map(q => {
                if (!q.question_img || q.question_img.trim() === "") return null;
                let imgPath = q.question_img.trim();
                // If the path doesn't already include a URL or slash, prefix it with /questionsPhoto/
                if (!imgPath.startsWith("http") && !imgPath.startsWith("/")) {
                    imgPath = `/questionsPhoto/${imgPath}`;
                }
                return imgPath;
            })
            .filter(Boolean); // Filter out empty strings/nulls
    }, [questionList]);

    // 3. Download images into Cache Storage automatically
    const downloadImages = useCallback(async (imagesToDownload) => {
        if (imagesToDownload.length === 0) {
            setComplete(true);
            return;
        }

        setDownloading(true);
        setComplete(false);
        setError("");
        setDownloaded(0);

        try {
            const cache = await caches.open(IMAGE_CACHE);
            let count = 0;

            for (const imageUrl of imagesToDownload) {
                const alreadyCached = await cache.match(imageUrl);

                if (!alreadyCached) {
                    const response = await fetch(imageUrl);

                    if (!response.ok) {
                        throw new Error(`Unable to download: ${imageUrl}`);
                    }
                    await cache.put(imageUrl, response.clone());
                }
                count++;
                setDownloaded(count);
            }

            setComplete(true);
        } catch (err) {
            console.error(err);
            setError("Unable to download questions. Please check your connection and try again.");
        } finally {
            setDownloading(false);
        }
    }, []);

    // 4. Automatically trigger download as soon as images are loaded from params
    useEffect(() => {
        if (questionList.length > 0) {
            if (dynamicImageList.length > 0) {
                downloadImages(dynamicImageList);
            } else {
                // If questions exist but have no images, complete instantly
                setComplete(true);
            }
        }
    }, [questionList, dynamicImageList, downloadImages]);

    const percentage = dynamicImageList.length === 0 ? 100 : Math.round((downloaded / dynamicImageList.length) * 100);

    return (
        <div className="qil-container">
            <div className="qil-card">
                <TopNavbar name="Prepare Questions" />

                <h3>Diagram base questions</h3>
                <br />
                <p className="qil-subtitle">
                    {complete ? "All question are ready!" : "Please wait downloading question..."}
                </p>

                {/* Summary badge showing items found */}
                <div className="qil-info-badge">
                    <span>Total Questions: <strong>{questionList.length}</strong></span>
                </div>

                {downloading && (
                    <div className="qil-progress-box">
                        <p className="qil-progress-text">Downloading {downloaded} / {dynamicImageList.length}</p>
                        <div className="qil-progress-bar-container">
                            <div className="qil-progress-bar-fill" style={{ width: `${percentage}%` }}></div>
                        </div>
                        <p className="qil-percentage">{percentage}%</p>
                    </div>
                )}

                {complete && (
                    <div className="qil-success-box">
                        <h3>✓ All questions are ready!</h3>
                        <Link
                            to={`/online-practics-questions/${subject}/${topic}/${type}`}
                            className="qil-btn qil-btn-success"
                        >
                            Start Practice Questions →
                        </Link>
                    </div>
                )}

                {error && (
                    <div>
                        <p className="qil-error-text">{error}</p>
                        <button
                            className="qil-btn qil-btn-primary"
                            onClick={() => downloadImages(dynamicImageList)}
                            style={{ marginTop: "10px" }}
                        >
                            Retry Download
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuestionImageLoad;